// Admin & Setup — everything editable per business, plus create/switch/delete businesses. Keeps the
// tool fully data-driven and multi-tenant: a new business defines its own ingredients/recipes/SKUs.
import { useState, type ChangeEvent } from "react";
import type { BusinessData, Canner, Channel, ChannelKind, CogsExtra, Ingredient, ProductType, Sku, Supplier } from "@shared/types.ts";
import { skuRecipeKey, massPerCanFromGL, recipeConversions, gramsPerMassUnit, defaultRunSizing } from "@shared/engine.ts";
import { useData } from "../state/store.tsx";
import { api } from "../lib/api.ts";
import { id as newId } from "../lib/ids.ts";
import { SegTabs, Modal, LabeledInput, Badge, EmptyState } from "../components/ui.tsx";
import { money } from "../lib/format.ts";

type Tab = "business" | "planning" | "channels" | "ingredients" | "recipes" | "skus" | "suppliers" | "canners";
const UOMS = ["Lbs", "Kg", "g", "oz", "L", "ea"];

export default function Admin() {
  const [tab, setTab] = useState<Tab>("business");
  return (
    <div className="space-y-4">
      <SegTabs
        tabs={[
          { id: "business", label: "Business" },
          { id: "planning", label: "Planning" },
          { id: "channels", label: "Channels" },
          { id: "ingredients", label: "Ingredients" },
          { id: "recipes", label: "Recipes" },
          { id: "skus", label: "SKUs" },
          { id: "suppliers", label: "Suppliers" },
          { id: "canners", label: "Canners" },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "business" && <BusinessTab />}
      {tab === "planning" && <PlanningTab />}
      {tab === "channels" && <ChannelsTab />}
      {tab === "ingredients" && <IngredientsTab />}
      {tab === "recipes" && <RecipesTab />}
      {tab === "skus" && <SkusTab />}
      {tab === "suppliers" && <SuppliersTab />}
      {tab === "canners" && <CannersTab />}
    </div>
  );
}

// ── Business settings + business management ──────────────────────────────────
function BusinessTab() {
  const { data, mutate, save, saving, registry, switchBusiness, seedLittleRick } = useData();
  const d = data!;
  const b = d.business;
  const [name, setName] = useState(b.name);
  const [market, setMarket] = useState(b.market || "");
  const [shopifyKey, setShopifyKey] = useState(b.shopifyKey || "");
  const [shopifyLocation, setShopifyLocation] = useState(b.shopifyLocation || "");
  const [demandShopifyOnly, setDemandShopifyOnly] = useState(!!b.demandShopifyOnly);
  const [amazonKey, setAmazonKey] = useState(b.amazonKey || "");
  const [unitLabel, setUnitLabel] = useState(b.unitLabel);
  const [massUnit, setMassUnit] = useState(b.massUnit || "lb");
  const [currency, setCurrency] = useState(b.currency);
  const [overagePct, setOverage] = useState(String(b.overagePct * 100));
  const [tiers, setTiers] = useState(b.cogsTierSizes.join(", "));
  const [unitVolumeL, setUnitVol] = useState(String(b.unitVolumeL));
  const [ownerEmails, setOwnerEmails] = useState((b.notify?.ownerEmails || []).join(", "));
  const [mfrEmails, setMfrEmails] = useState((b.notify?.mfrEmails || []).join(", "));

  const emailList = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const saveNotify = () =>
    mutate((draft) => {
      draft.business.notify = { ownerEmails: emailList(ownerEmails), mfrEmails: emailList(mfrEmails) };
    });
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const sendTest = async (audience: "owner" | "mfr") => {
    setTesting(true); setTestMsg(null);
    try {
      const r = await api.notifyTest({ businessId: b.id, audience });
      if (r.ok) setTestMsg({ ok: true, text: `✓ Test sent to ${r.to} ${audience} address${r.to !== 1 ? "es" : ""}. Check the inbox (and spam).` });
      else setTestMsg({ ok: false, text: r.error || "Send failed." });
    } catch (e) {
      setTestMsg({ ok: false, text: e instanceof Error ? e.message : "Send failed." });
    } finally { setTesting(false); }
  };

  const saveSettings = () =>
    mutate((draft) => {
      draft.business.name = name;
      draft.business.market = market || undefined;
      draft.business.shopifyKey = shopifyKey || undefined;
      draft.business.shopifyLocation = shopifyLocation.trim() || undefined;
      draft.business.demandShopifyOnly = demandShopifyOnly || undefined;
      draft.business.amazonKey = amazonKey || undefined;
      draft.business.unitLabel = unitLabel || "unit";
      draft.business.massUnit = massUnit || "lb";
      draft.business.currency = currency || "USD";
      draft.business.overagePct = (Number(overagePct) || 0) / 100;
      draft.business.unitVolumeL = Number(unitVolumeL) || 0;
      draft.business.cogsTierSizes = tiers.split(",").map((s) => Number(s.trim())).filter((n) => n > 0);
    });

  const createBusiness = async () => {
    const nm = prompt("New business name?");
    if (!nm) return;
    const nb: BusinessData = {
      business: {
        id: newId("biz"), name: nm, unitLabel: "unit", unitVolumeL: 0, currency: "USD",
        overagePct: 0.05, cogsTierSizes: [5000, 10000, 15000, 20000],
        conversions: { barrelGallon: 31, gallonL: 3.78541, lCan: 0.355, ozCan: 12, lbsG: 453.592, ozG: 28.34952, eurUsd: 1.17 },
        createdAt: Date.now(),
      },
      suppliers: [], ingredients: [], skus: [], recipes: {}, cogsTiers: [], canners: [], runs: [], orders: [], stock: [],
    };
    await save(nb);
    switchBusiness(nb.business.id);
  };

  const deleteBusiness = async () => {
    if (!confirm(`Delete business "${b.name}" and ALL its data? This cannot be undone.`)) return;
    await api.deleteBusiness(b.id);
    location.reload();
  };

  return (
    <div className="space-y-4">
      <div className="card space-y-4">
        <h3 className="text-sm font-semibold">Business settings</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <LabeledInput label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></LabeledInput>
          <LabeledInput label="Market" hint="Free label, e.g. US / UK"><input className="input" value={market} onChange={(e) => setMarket(e.target.value)} /></LabeledInput>
          <LabeledInput label="Shopify store key" hint="US or UK — matches SHOPIFY_<KEY>_* env vars (blank = no Shopify)"><input className="input" value={shopifyKey} onChange={(e) => setShopifyKey(e.target.value)} /></LabeledInput>
          <LabeledInput label="Shopify location filter" hint="Only count inventory at this location (name, e.g. Mission Fulfilment). Blank = all locations."><input className="input" value={shopifyLocation} onChange={(e) => setShopifyLocation(e.target.value)} placeholder="all locations" /></LabeledInput>
          <label className="flex items-start gap-2 text-sm sm:col-span-3 mt-1"><input type="checkbox" className="mt-0.5" checked={demandShopifyOnly} onChange={(e) => setDemandShopifyOnly(e.target.checked)} /> <span>Demand from <span className="font-medium">Shopify only</span> <span className="text-muted font-normal">— ignore Amazon &amp; manual channels when computing inventory + velocity (use when Shopify is your single source of on-hand stock).</span></span></label>
          <LabeledInput label="Amazon store key" hint="US or UK — matches AMAZON_SP_<KEY>_* env vars (blank = no Amazon)"><input className="input" value={amazonKey} onChange={(e) => setAmazonKey(e.target.value)} /></LabeledInput>
          <LabeledInput label="Unit label" hint="What one finished item is called"><input className="input" value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} /></LabeledInput>
          <LabeledInput label="Mass unit" hint="Recipe mass unit: lb or kg"><input className="input" value={massUnit} onChange={(e) => setMassUnit(e.target.value)} /></LabeledInput>
          <LabeledInput label="Currency"><input className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} /></LabeledInput>
          <LabeledInput label="Overage %" hint="Production buffer added to required amounts"><input type="number" step="any" className="input" value={overagePct} onChange={(e) => setOverage(e.target.value)} /></LabeledInput>
          <LabeledInput label="Litres per unit" hint="For volumetric recipe entry (0 if N/A)"><input type="number" step="any" className="input" value={unitVolumeL} onChange={(e) => setUnitVol(e.target.value)} /></LabeledInput>
          <LabeledInput label="COGS run-size tiers" hint="Comma-separated"><input className="input" value={tiers} onChange={(e) => setTiers(e.target.value)} /></LabeledInput>
        </div>
        <div className="flex justify-end"><button className="btn-primary btn-sm" disabled={saving} onClick={saveSettings}>{saving ? "…" : "Save settings"}</button></div>
      </div>

      <div className="card space-y-4">
        <div>
          <h3 className="text-sm font-semibold">Notifications</h3>
          <p className="text-muted text-xs mt-0.5">Who gets emailed. Milestones &amp; alerts (delays, low stock, production recorded) go to the <span className="font-medium">owner</span> addresses; action prompts (confirm the date, ingredients missing) go to the <span className="font-medium">manufacturer</span> addresses. Leave blank for in-app-only. Email sending needs <code className="text-[11px]">RESEND_API_KEY</code> + <code className="text-[11px]">NOTIFY_EMAIL_FROM</code> set in Netlify.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <LabeledInput label="Owner emails" hint="Comma-separated — you / the Little Rick team"><input className="input" value={ownerEmails} onChange={(e) => setOwnerEmails(e.target.value)} placeholder="you@drinklittlerick.com" /></LabeledInput>
          <LabeledInput label="Manufacturer emails" hint="Comma-separated — the canner / co-packer"><input className="input" value={mfrEmails} onChange={(e) => setMfrEmails(e.target.value)} placeholder="ops@canner.com" /></LabeledInput>
        </div>
        {testMsg && <div className={`text-xs rounded-lg px-3 py-2 ${testMsg.ok ? "bg-ok/10 text-ok" : "bg-danger/10 text-danger"}`}>{testMsg.text}</div>}
        <div className="flex flex-wrap justify-end gap-2">
          <button className="btn-sm border border-edge hover:bg-paper" disabled={testing} onClick={() => sendTest("owner")}>{testing ? "…" : "Send test to owner"}</button>
          <button className="btn-sm border border-edge hover:bg-paper" disabled={testing} onClick={() => sendTest("mfr")}>{testing ? "…" : "Send test to manufacturer"}</button>
          <button className="btn-primary btn-sm" disabled={saving} onClick={saveNotify}>{saving ? "…" : "Save notifications"}</button>
        </div>
      </div>

      <div className="card space-y-3">
        <h3 className="text-sm font-semibold">Businesses</h3>
        <div className="flex flex-wrap gap-2">
          {registry.businesses.map((rb) => (
            <button key={rb.id} onClick={() => switchBusiness(rb.id)} className={`btn-sm border ${rb.id === b.id ? "border-brand text-brand bg-brand/5" : "border-edge hover:bg-paper"}`}>{rb.name}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <button className="btn-primary btn-sm" onClick={createBusiness}>+ New business</button>
          <button className="btn-sm border border-edge hover:bg-paper" disabled={saving} onClick={() => void seedLittleRick()}>Import Little Rick — US</button>
          <button className="btn-sm text-danger hover:bg-danger/5 ml-auto" onClick={deleteBusiness}>Delete “{b.name}”</button>
        </div>
      </div>
    </div>
  );
}

// ── Planning assumptions ─────────────────────────────────────────────────────
function PlanningTab() {
  const { data, mutate, saving } = useData();
  const d = data!;
  const p = d.business.planning || { leadTimeDays: 60, safetyStockDays: 30, runwayDays: 150, soonExtraDays: 30 };
  const dist = d.business.distribution || { rebalanceLeadDays: 21, daysToSendDays: 30 };
  const [lead, setLead] = useState(String(p.leadTimeDays));
  const [safety, setSafety] = useState(String(p.safetyStockDays));
  const [runway, setRunway] = useState(String(p.runwayDays ?? 150));
  const [soon, setSoon] = useState(String(p.soonExtraDays));
  const [tbr, setTbr] = useState(String(d.business.targetBookedRuns ?? 2));
  const rs = d.business.runSizing || defaultRunSizing(d.business.market);
  const [rsMin, setRsMin] = useState(String(rs.minRun || ""));
  const [rsMax, setRsMax] = useState(String(rs.maxRun || ""));
  const [rsStep, setRsStep] = useState(String(rs.step || 1000));
  const [rebLead, setRebLead] = useState(String(dist.rebalanceLeadDays));
  const [send, setSend] = useState(String(dist.daysToSendDays));
  const [pp, setPp] = useState(String(dist.packsPerPallet ?? 192));
  const [mixCF, setMixCF] = useState(String(dist.mixCansPerFlavour ?? 3));
  const [defPack, setDefPack] = useState(String(dist.defaultPackSize ?? 12));

  const save = () => mutate((draft) => {
    draft.business.planning = { leadTimeDays: +lead || 0, safetyStockDays: +safety || 0, runwayDays: +runway || 0, soonExtraDays: +soon || 0 };
    draft.business.targetBookedRuns = +tbr || 0;
    draft.business.runSizing = { minRun: +rsMin || 0, maxRun: +rsMax || 0, step: +rsStep || 1000 };
    draft.business.distribution = { rebalanceLeadDays: +rebLead || 0, daysToSendDays: +send || 0, packsPerPallet: +pp || 192, mixCansPerFlavour: +mixCF || 0, defaultPackSize: +defPack || 12 };
  });

  return (
    <div className="space-y-4">
    <div className="card space-y-4">
      <div>
        <h3 className="text-sm font-semibold">Demand planning assumptions</h3>
        <p className="text-muted text-xs mt-0.5">Drives “what to produce &amp; when” on the Demand screen.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <LabeledInput label="Lead time (days)"><input type="number" className="input" value={lead} onChange={(e) => setLead(e.target.value)} /></LabeledInput>
        <LabeledInput label="Safety stock (days)"><input type="number" className="input" value={safety} onChange={(e) => setSafety(e.target.value)} /></LabeledInput>
        <LabeledInput label="Days of runway" hint="Each production covers this many days of sales (× SKU velocity)"><input type="number" className="input" value={runway} onChange={(e) => setRunway(e.target.value)} /></LabeledInput>
        <LabeledInput label="“Soon” extra days" hint="Order-soon window above lead+safety"><input type="number" className="input" value={soon} onChange={(e) => setSoon(e.target.value)} /></LabeledInput>
        <LabeledInput label="Productions to keep booked" hint="Always keep this many runs in the pipeline (Booking flags any missing)"><input type="number" className="input" value={tbr} onChange={(e) => setTbr(e.target.value)} /></LabeledInput>
      </div>
      <div>
        <h3 className="text-sm font-semibold pt-2">Production run sizing</h3>
        <p className="text-muted text-xs mt-0.5">Valid run sizes from the canner's daily capacity. A run day makes min–max {d.business.unitLabel}s; recommended productions snap up to n×min…n×max. Leave <span className="font-medium">max</span> blank for no bands (just a floor + step). UK: 15000 / 20000 / 1000 · US: 5000 / — / 1000.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <LabeledInput label="Min / preferred per day" hint="Batch minimum (UK 15000, US 5000)"><input type="number" className="input" value={rsMin} onChange={(e) => setRsMin(e.target.value)} /></LabeledInput>
        <LabeledInput label="Max per day" hint="Upper band (UK 20000; blank = no jumps)"><input type="number" className="input" value={rsMax} onChange={(e) => setRsMax(e.target.value)} /></LabeledInput>
        <LabeledInput label="Increment" hint="Step within a band (1000)"><input type="number" className="input" value={rsStep} onChange={(e) => setRsStep(e.target.value)} /></LabeledInput>
      </div>
      <div>
        <h3 className="text-sm font-semibold pt-2">Distribution / rebalance</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <LabeledInput label="Rebalance lead (days)"><input type="number" className="input" value={rebLead} onChange={(e) => setRebLead(e.target.value)} /></LabeledInput>
        <LabeledInput label="Days of stock to send"><input type="number" className="input" value={send} onChange={(e) => setSend(e.target.value)} /></LabeledInput>
        <LabeledInput label="Packs per pallet" hint="Retail packs per pallet (UK: 192)"><input type="number" className="input" value={pp} onChange={(e) => setPp(e.target.value)} /></LabeledInput>
        <LabeledInput label="Mix Pack cans/flavour" hint="Cans of each single flavour in a Mix Pack (UK: 3 → 12 total)"><input type="number" className="input" value={mixCF} onChange={(e) => setMixCF(e.target.value)} /></LabeledInput>
        <LabeledInput label="Default pack size" hint="Fallback retail pack size when a SKU has none (UK: 12)"><input type="number" className="input" value={defPack} onChange={(e) => setDefPack(e.target.value)} /></LabeledInput>
      </div>
      <div className="flex justify-end"><button className="btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? "…" : "Save"}</button></div>
    </div>
    <ProductTypesCard />
    <CogsExtrasCard />
    </div>
  );
}

// Product types — cans (tolling tiers), spirit bottles (per-bottle build), gummies (purchased).
// Drives COGS make-up + units-per-pallet. Edit via modal.
function ProductTypesCard() {
  const { data, mutate } = useData();
  const cur = data!.business.currency;
  const types = data!.business.productTypes || [];
  const [edit, setEdit] = useState<ProductType | null>(null);
  const [creating, setCreating] = useState(false);
  const summary = (t: ProductType) => {
    const b = (t.build || []).reduce((a, x) => a + (Number(x.amount) || 0), 0);
    if (t.usesTiers) return b > 0 ? `tolling tiers · incl ${money(b, cur)} build` : "tolling tiers";
    return b > 0 ? `build ${money(b, cur)}/unit` : (t.purchased ? "purchased" : "—");
  };
  // Adds/normalises the standard set (by id), keeping any custom types you've added.
  const addStandard = () => mutate((draft) => {
    const std: ProductType[] = [
      { id: "can", label: "12oz Cans", usesTiers: true, unitsPerPallet: 2496, build: [{ name: "Cans", amount: 0.3 }] },
      { id: "can16", label: "16oz Cans", usesTiers: true, unitsPerPallet: 2496, build: [{ name: "Cans", amount: 0.35 }] },
      { id: "bottle", label: "Spirit bottles", unitsPerPallet: 1150, build: [{ name: "Filling", amount: 2 }, { name: "Bottle", amount: 0.8 }, { name: "Cork", amount: 0.3 }, { name: "Label", amount: 0.1 }] },
      { id: "gummy", label: "Gummies", purchased: true, build: [{ name: "Gummies", amount: 0 }, { name: "Packaging", amount: 0 }] },
    ];
    const others = (draft.business.productTypes || []).filter((t) => !std.some((s) => s.id === t.id));
    draft.business.productTypes = [...std, ...others];
  });
  return (
    <div className="card space-y-3">
      <div className="flex justify-between items-center gap-3">
        <div><h3 className="text-sm font-semibold">Product types</h3><p className="text-muted text-xs mt-0.5">Each SKU has a type — 12oz/16oz cans, spirit bottles, gummies… Cans use the tolling tiers; bottles/gummies use a per-unit build. Units/pallet drives per-pallet freight + distribution.</p></div>
        <div className="flex gap-2 shrink-0">
          <button className="btn-sm border border-edge hover:bg-paper" onClick={addStandard}>Standard set</button>
          <button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Type</button>
        </div>
      </div>
      {types.length === 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-paper border border-edge/70 p-3 text-sm">
          <span className="text-muted">No product types yet — click <span className="font-medium">Standard set</span> to add 12oz Cans · 16oz Cans · Spirit bottles · Gummies.</span>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[480px]">
          <thead><tr><th className="th">Type</th><th className="th text-right">Units / pallet</th><th className="th text-right">Pallets / truck</th><th className="th">Production</th><th className="th"></th></tr></thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.id}>
                <td className="td font-medium">{t.label} <span className="text-muted font-normal text-[11px]">({t.id})</span></td>
                <td className="td text-right tabular-nums">{t.unitsPerPallet || "—"}</td>
                <td className="td text-right tabular-nums">{t.palletsPerTruck || <span className="text-muted/60">default</span>}</td>
                <td className="td text-muted">{summary(t)}</td>
                <td className="td text-right"><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(t)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {(creating || edit) && (
        <ProductTypeModal type={edit} isNew={!edit} onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (t) => {
            await mutate((draft) => {
              draft.business.productTypes = draft.business.productTypes || [];
              const i = draft.business.productTypes.findIndex((x) => x.id === t.id);
              if (i >= 0) draft.business.productTypes[i] = t; else draft.business.productTypes.push(t);
            });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? async () => { await mutate((draft) => { draft.business.productTypes = (draft.business.productTypes || []).filter((x) => x.id !== edit.id); }); setEdit(null); } : undefined} />
      )}
    </div>
  );
}

function ProductTypeModal({ type, isNew, onClose, onSave, onDelete }: {
  type: ProductType | null; isNew: boolean; onClose: () => void; onSave: (t: ProductType) => void; onDelete?: () => void;
}) {
  const { saving } = useData();
  const [id, setId] = useState(type?.id || "");
  const [label, setLabel] = useState(type?.label || "");
  const [unitsPerPallet, setUpp] = useState(type?.unitsPerPallet != null ? String(type.unitsPerPallet) : "");
  const [palletsPerTruck, setPpt] = useState(type?.palletsPerTruck != null ? String(type.palletsPerTruck) : "");
  const [usesTiers, setUsesTiers] = useState(!!type?.usesTiers);
  const [purchased, setPurchased] = useState(!!type?.purchased);
  const [build, setBuild] = useState<{ name: string; amount: string; received: boolean }[]>((type?.build || []).map((b) => ({ name: b.name, amount: String(b.amount), received: !!b.received })));
  const setB = (i: number, k: "name" | "amount", v: string) => setBuild((s) => s.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
  const setBRcv = (i: number, v: boolean) => setBuild((s) => s.map((b, idx) => (idx === i ? { ...b, received: v } : b)));
  const buildSum = build.reduce((a, b) => a + (Number(b.amount) || 0), 0);
  return (
    <Modal title={type ? `Edit ${type.label}` : "New product type"} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Label"><input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Spirit bottles" /></LabeledInput>
          <LabeledInput label="ID" hint={isNew ? "lowercase, no spaces — SKUs reference this" : "fixed (SKUs reference it)"}>
            <input className="input" value={id} disabled={!isNew} onChange={(e) => setId(e.target.value.trim().toLowerCase().replace(/\s+/g, "-"))} placeholder="bottle" />
          </LabeledInput>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Units per pallet" hint="e.g. 2496 cans, 1150 bottles — drives freight & distribution"><input type="number" className="input" value={unitsPerPallet} onChange={(e) => setUpp(e.target.value)} /></LabeledInput>
          <LabeledInput label="Pallets per truck" hint="Per-type truck capacity (blank = business default)"><input type="number" className="input" value={palletsPerTruck} onChange={(e) => setPpt(e.target.value)} /></LabeledInput>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={usesTiers} onChange={(e) => setUsesTiers(e.target.checked)} /> Uses tolling tiers (cans) — the tier is the production total; build components below are <span className="font-medium">carved out</span> of it</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={purchased} onChange={(e) => setPurchased(e.target.checked)} /> Purchased (bought finished — no recipe, e.g. gummies)</label>
        <div className="rounded-lg border border-edge/70 p-3 space-y-2">
          <div className="flex items-center justify-between"><span className="label mb-0">Production components (per unit)</span><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setBuild((s) => [...s, { name: "", amount: "", received: false }])}>+ Component</button></div>
          <p className="text-[11px] text-muted">Cans: the can itself. Bottles: fill, bottle, cork, label, caps, boxes. Gummies: gummies + packaging. Tick <span className="font-medium">Received</span> for the physical items the co-packer confirms on the Packaging checklist (bottles/labels/corks/caps — not the fill).</p>
          {build.map((b, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className="input py-1 flex-1" placeholder="Component (e.g. Cork)" value={b.name} onChange={(e) => setB(i, "name", e.target.value)} />
              <input type="number" step="any" className="input py-1 w-20 text-right" placeholder="0.00" value={b.amount} onChange={(e) => setB(i, "amount", e.target.value)} />
              <label className="flex items-center gap-1 text-[11px] text-muted shrink-0" title="Physical delivery the co-packer confirms on receipt"><input type="checkbox" checked={b.received} onChange={(e) => setBRcv(i, e.target.checked)} /> Recd</label>
              <button className="btn-sm text-danger hover:bg-danger/5" onClick={() => setBuild((s) => s.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}
          {build.length > 0 && <div className="text-xs text-muted text-right">{usesTiers ? "Carved from tolling" : "Build total"}: <span className="font-medium text-ink">{buildSum.toFixed(2)}</span> / unit</div>}
        </div>
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !id || !label} onClick={() => onSave({
              id, label,
              unitsPerPallet: unitsPerPallet === "" ? undefined : Number(unitsPerPallet),
              palletsPerTruck: palletsPerTruck === "" ? undefined : Number(palletsPerTruck),
              usesTiers: usesTiers || undefined,
              purchased: purchased || undefined,
              build: build.filter((b) => b.name.trim()).map((b) => ({ name: b.name.trim(), amount: Number(b.amount) || 0, received: b.received || undefined })),
            })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}

// COGS extras — inbound freight, COA/testing, other adders (per-can or per-run). Edit via modal.
const BASIS_LABEL: Record<CogsExtra["basis"], string> = { perCan: "per can", perRun: "per run", perSKU: "per SKU", perPallet: "per pallet", perTruck: "per truck" };
function CogsExtrasCard() {
  const { data, mutate } = useData();
  const cur = data!.business.currency;
  const extras = data!.business.cogsExtras || [];
  const ship = data!.business.shipping || { cansPerPallet: 0, palletsPerTruck: 0 };
  const [edit, setEdit] = useState<CogsExtra | null>(null);
  const [creating, setCreating] = useState(false);
  const [shipEdit, setShipEdit] = useState(false);
  return (
    <div className="card space-y-3">
      <div className="flex justify-between items-center">
        <div><h3 className="text-sm font-semibold">COGS extras</h3><p className="text-muted text-xs mt-0.5">Added to every unit's COGS — e.g. inbound freight, COA / lab testing. Per-run &amp; per-SKU spread over the run size; per-pallet &amp; per-truck use the shipping config (freight = ⌈pallets/truck⌉×truck-cost + pallets×pallet-cost).</p></div>
        <button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Cost</button>
      </div>
      <div className="flex items-center justify-between gap-4 rounded-lg bg-paper border border-edge/70 p-2.5 text-sm">
        <span className="text-muted"><span className="font-medium text-ink">Default</span> shipping: <span className="text-ink font-medium">{ship.cansPerPallet || "—"}</span> units/pallet · <span className="text-ink font-medium">{ship.palletsPerTruck || "—"}</span> pallets/truck <span className="text-muted/70">— per-type values (spirits, gummies…) set in Planning → Product types</span></span>
        <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setShipEdit(true)}>Edit</button>
      </div>
      {extras.length > 0 && (
        <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[480px]">
          <thead><tr><th className="th">Name</th><th className="th text-right">Amount</th><th className="th">Basis</th><th className="th">Applies to</th><th className="th"></th></tr></thead>
          <tbody>
            {extras.map((e) => (
              <tr key={e.id}>
                <td className="td font-medium">{e.name}</td>
                <td className="td text-right tabular-nums">{money(e.amount, cur)}</td>
                <td className="td text-muted">{BASIS_LABEL[e.basis]}</td>
                <td className="td text-muted">{e.productType ? ((data!.business.productTypes || []).find((t) => t.id === e.productType)?.label || e.productType) : "All"}</td>
                <td className="td text-right"><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(e)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {(creating || edit) && (
        <CogsExtraModal extra={edit} onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (patch) => {
            await mutate((draft) => {
              if (edit) { const e = (draft.business.cogsExtras || []).find((x) => x.id === edit.id); if (e) Object.assign(e, patch); }
              else draft.business.cogsExtras = [...(draft.business.cogsExtras || []), { id: newId("cx"), ...patch }];
            });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? async () => { await mutate((draft) => { draft.business.cogsExtras = (draft.business.cogsExtras || []).filter((x) => x.id !== edit.id); }); setEdit(null); } : undefined} />
      )}
      {shipEdit && (
        <ShippingModal ship={ship} onClose={() => setShipEdit(false)}
          onSave={async (s) => { await mutate((draft) => { draft.business.shipping = s; }); setShipEdit(false); }} />
      )}
    </div>
  );
}

function CogsExtraModal({ extra, onClose, onSave, onDelete }: {
  extra: CogsExtra | null; onClose: () => void; onSave: (p: Omit<CogsExtra, "id">) => void; onDelete?: () => void;
}) {
  const { data, saving } = useData();
  const types = data!.business.productTypes || [];
  const [name, setName] = useState(extra?.name || "");
  const [amount, setAmount] = useState(extra?.amount != null ? String(extra.amount) : "");
  const [basis, setBasis] = useState<CogsExtra["basis"]>(extra?.basis || "perCan");
  const [productType, setProductType] = useState(extra?.productType || "");
  return (
    <Modal title={extra ? "Edit cost" : "New cost"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Inbound freight" /></LabeledInput>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Amount"><input type="number" step="any" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} /></LabeledInput>
          <LabeledInput label="Basis">
            <select className="input" value={basis} onChange={(e) => setBasis(e.target.value as CogsExtra["basis"])}>
              {(Object.keys(BASIS_LABEL) as CogsExtra["basis"][]).map((b) => <option key={b} value={b}>{BASIS_LABEL[b]}</option>)}
            </select>
          </LabeledInput>
        </div>
        {types.length > 0 && (
          <LabeledInput label="Applies to" hint="Limit this cost to one product type (per-pallet uses that type's units/pallet)">
            <select className="input" value={productType} onChange={(e) => setProductType(e.target.value)}>
              <option value="">All product types</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </LabeledInput>
        )}
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !name} onClick={() => onSave({ name, amount: Number(amount) || 0, basis, productType: productType || undefined })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}

function ShippingModal({ ship, onClose, onSave }: {
  ship: { cansPerPallet: number; palletsPerTruck: number }; onClose: () => void; onSave: (s: { cansPerPallet: number; palletsPerTruck: number }) => void;
}) {
  const { saving } = useData();
  const [cpp, setCpp] = useState(ship.cansPerPallet ? String(ship.cansPerPallet) : "");
  const [ppt, setPpt] = useState(ship.palletsPerTruck ? String(ship.palletsPerTruck) : "");
  return (
    <Modal title="Shipping config" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Cans per pallet"><input type="number" className="input" value={cpp} onChange={(e) => setCpp(e.target.value)} /></LabeledInput>
          <LabeledInput label="Pallets per truck"><input type="number" className="input" value={ppt} onChange={(e) => setPpt(e.target.value)} /></LabeledInput>
        </div>
        <div className="flex justify-end gap-2 pt-1"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={saving} onClick={() => onSave({ cansPerPallet: Number(cpp) || 0, palletsPerTruck: Number(ppt) || 0 })}>{saving ? "…" : "Save"}</button></div>
      </div>
    </Modal>
  );
}

// ── Channels ─────────────────────────────────────────────────────────────────
const CHANNEL_KINDS: ChannelKind[] = ["shopify", "tiktok", "amazon", "warehouse", "other"];
function ChannelsTab() {
  const { data, mutate } = useData();
  const d = data!;
  const channels = d.business.channels || [];

  const [edit, setEdit] = useState<Channel | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center"><h3 className="text-sm font-semibold">Sales channels / warehouses</h3><button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Channel</button></div>
      <p className="text-muted text-xs">Sources for Demand + Distribution. “Synced” pulls live from the connection (Shopify or Amazon); un-synced channels (e.g. TikTok) use the manual figures you enter in Distribution.</p>
      {channels.length === 0 ? <div className="card"><EmptyState title="No channels yet" /></div> : (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead><tr><th className="th">Name</th><th className="th">Kind</th><th className="th text-right">Weight</th><th className="th">Synced</th><th className="th">Pack sizes</th><th className="th"></th></tr></thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.id}>
                  <td className="td font-medium">{c.name}</td>
                  <td className="td text-muted">{c.kind}</td>
                  <td className="td text-right tabular-nums">{c.weight ?? 1}</td>
                  <td className="td">{c.auto ? <span className="text-ok">✓</span> : <span className="text-muted">—</span>}</td>
                  <td className="td text-muted">{c.auto ? "— synced" : (c.packSizes || []).join(", ") || "—"}</td>
                  <td className="td text-right"><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(c)}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(creating || edit) && (
        <ChannelModal channel={edit} onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (patch) => {
            await mutate((draft) => {
              if (edit) { const c = (draft.business.channels || []).find((x) => x.id === edit.id); if (c) Object.assign(c, patch); }
              else draft.business.channels = [...(draft.business.channels || []), { id: newId("ch"), ...patch }];
            });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? async () => { await mutate((draft) => { draft.business.channels = (draft.business.channels || []).filter((x) => x.id !== edit.id); }); setEdit(null); } : undefined} />
      )}
    </div>
  );
}

function ChannelModal({ channel, onClose, onSave, onDelete }: {
  channel: Channel | null; onClose: () => void; onSave: (p: Omit<Channel, "id">) => void; onDelete?: () => void;
}) {
  const { saving } = useData();
  const [name, setName] = useState(channel?.name || "");
  const [kind, setKind] = useState<ChannelKind>(channel?.kind || "other");
  const [auto, setAuto] = useState(!!channel?.auto);
  const [weight, setWeight] = useState(channel?.weight != null ? String(channel.weight) : "1");
  const [packSizes, setPackSizes] = useState((channel?.packSizes || []).join(", "));
  return (
    <Modal title={channel ? "Edit channel" : "New channel"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></LabeledInput>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Kind"><select className="input" value={kind} onChange={(e) => setKind(e.target.value as ChannelKind)}>{CHANNEL_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select></LabeledInput>
          <LabeledInput label="Weight" hint="Relative split weight"><input type="number" step="any" className="input" value={weight} onChange={(e) => setWeight(e.target.value)} /></LabeledInput>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Synced (pull inventory/velocity live from Shopify or Amazon)</label>
        {!auto && <LabeledInput label="Pack sizes" hint="Formats this channel sells (cans per pack), comma-separated — e.g. 1, 4, 12"><input className="input" value={packSizes} onChange={(e) => setPackSizes(e.target.value)} placeholder="e.g. 1, 4, 12" /></LabeledInput>}
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !name} onClick={() => onSave({ name, kind, auto, weight: Number(weight) || 1, packSizes: auto ? undefined : packSizes.split(",").map((x) => Number(x.trim())).filter((n) => n > 0) })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}

// ── Ingredients ──────────────────────────────────────────────────────────────
function IngredientsTab() {
  const { data, mutate, saving } = useData();
  const d = data!;
  const [edit, setEdit] = useState<Ingredient | null>(null);
  const [creating, setCreating] = useState(false);

  const supplierNames = d.suppliers.map((s) => s.name);

  const remove = (idv: string) => mutate((draft) => { draft.ingredients = draft.ingredients.filter((i) => i.id !== idv); });

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center"><h3 className="text-sm font-semibold">Ingredients &amp; prices</h3><button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Ingredient</button></div>
      <div className="card overflow-x-auto">
        <table className="w-full min-w-[560px]">
          <thead><tr><th className="th">Name</th><th className="th">Supplier</th><th className="th text-right">$/{d.business.massUnit || "lb"}</th><th className="th">Pack UoM</th><th className="th"></th></tr></thead>
          <tbody>
            {d.ingredients.map((i) => (
              <tr key={i.id}>
                <td className="td font-medium">{i.name}</td>
                <td className="td text-muted">{i.supplier || "—"}</td>
                <td className="td text-right tabular-nums">{money(i.pricePerLb, d.business.currency)}</td>
                <td className="td text-muted">{i.packUoM || "—"}</td>
                <td className="td text-right"><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(i)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(creating || edit) && (
        <IngredientModal ingredient={edit} suppliers={supplierNames} saving={saving} massUnit={d.business.massUnit || "lb"}
          onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (ing) => {
            await mutate((draft) => {
              if (edit) { const t = draft.ingredients.find((x) => x.id === edit.id); if (t) Object.assign(t, ing); }
              else draft.ingredients.push({ id: newId("ing"), ...ing } as Ingredient);
            });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? () => { remove(edit.id); setEdit(null); } : undefined}
        />
      )}
    </div>
  );
}

function IngredientModal({ ingredient, suppliers, saving, massUnit, onClose, onSave, onDelete }: {
  ingredient: Ingredient | null; suppliers: string[]; saving: boolean; massUnit: string;
  onClose: () => void; onSave: (i: Omit<Ingredient, "id">) => void; onDelete?: () => void;
}) {
  const [name, setName] = useState(ingredient?.name || "");
  const [supplier, setSupplier] = useState(ingredient?.supplier || "");
  const [pricePerLb, setPrice] = useState(ingredient?.pricePerLb != null ? String(ingredient.pricePerLb) : "");
  const [packUoM, setPack] = useState(ingredient?.packUoM || "");
  const [packSize, setPackSize] = useState(ingredient?.packSize != null ? String(ingredient.packSize) : "");
  const [moq, setMoq] = useState(ingredient?.moq != null ? String(ingredient.moq) : "");
  return (
    <Modal title={ingredient ? "Edit ingredient" : "New ingredient"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></LabeledInput>
        <LabeledInput label="Supplier">
          <select className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)}>
            <option value="">— select supplier —</option>
            {supplier && !suppliers.includes(supplier) && <option value={supplier}>{supplier}</option>}
            {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {suppliers.length === 0 && <p className="text-[11px] text-muted mt-1">No suppliers yet — add them in the Suppliers tab first.</p>}
        </LabeledInput>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label={`Price / ${massUnit}`}><input type="number" step="any" className="input" value={pricePerLb} onChange={(e) => setPrice(e.target.value)} /></LabeledInput>
          <LabeledInput label="Pack UoM">
            <select className="input" value={packUoM} onChange={(e) => setPack(e.target.value)}>
              <option value="">—</option>
              {packUoM && !UOMS.includes(packUoM) && <option value={packUoM}>{packUoM}</option>}
              {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </LabeledInput>
          <LabeledInput label={`Pack size (${massUnit})`} hint="Orders round up to whole packs"><input type="number" step="any" className="input" value={packSize} onChange={(e) => setPackSize(e.target.value)} /></LabeledInput>
          <LabeledInput label={`MOQ (${massUnit})`} hint="Minimum order quantity"><input type="number" step="any" className="input" value={moq} onChange={(e) => setMoq(e.target.value)} /></LabeledInput>
        </div>
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !name} onClick={() => onSave({ name, supplier, pricePerLb: Number(pricePerLb) || 0, packUoM, packSize: packSize === "" ? undefined : Number(packSize), moq: moq === "" ? undefined : Number(moq) })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}

// ── Recipes ──────────────────────────────────────────────────────────────────
function RecipesTab() {
  const { data, mutate, saving } = useData();
  const d = data!;
  const activeSkus = d.skus.filter((s) => !s.assembled && !s.purchased);
  const [skuId, setSkuId] = useState(activeSkus[0]?.id || "");
  const sku = activeSkus.find((s) => s.id === skuId);
  const key = sku ? skuRecipeKey(sku) : "";
  const lines = (key && d.recipes[key]) || [];

  const massUnit = d.business.massUnit || "lb";
  const vol = d.business.unitVolumeL || 0;
  const conv = d.business.conversions;

  // g:L is the input; mass-per-unit (used by the engine) is derived from it.
  const setGL = (i: number, gPerL: number) =>
    mutate((draft) => {
      const ln = draft.recipes[key]?.[i];
      if (!ln) return;
      ln.gPerL = gPerL;
      ln.lbsPerCan = massPerCanFromGL(gPerL, vol, massUnit);
    });
  const addLine = (ingredient: string) =>
    mutate((draft) => { draft.recipes[key] = draft.recipes[key] || []; if (!draft.recipes[key].some((l) => l.ingredient === ingredient)) draft.recipes[key].push({ ingredient, gPerL: 0, lbsPerCan: 0 }); });
  const removeLine = (i: number) => mutate((draft) => { draft.recipes[key]?.splice(i, 1); });
  const [editIdx, setEditIdx] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <h3 className="text-sm font-semibold">Recipes <span className="text-muted font-normal">· grams per litre (g:L)</span></h3>
        <select className="input w-auto py-1.5" value={skuId} onChange={(e) => setSkuId(e.target.value)}>
          {activeSkus.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      {!sku ? <div className="card"><EmptyState title="No SKUs yet" sub="Create SKUs first, then define their recipes." /></div> : (
        <div className="card overflow-x-auto">
          <p className="text-muted text-xs mb-2">Enter <b>g:L</b> (grams per litre of finished drink). The rest is derived for reference — Oz:L, Lbs:BBL, and {massUnit}/{d.business.unitLabel} (used by ordering &amp; COGS).</p>
          <table className="w-full min-w-[560px]">
            <thead><tr>
              <th className="th">Ingredient</th>
              <th className="th text-right">g : L (input)</th>
              <th className="th text-right">Oz : L</th>
              <th className="th text-right">Lbs : BBL</th>
              <th className="th text-right">{massUnit} / {d.business.unitLabel}</th>
              <th className="th"></th>
            </tr></thead>
            <tbody>
              {lines.map((l, i) => {
                const gl = l.gPerL ?? (l.lbsPerCan && vol ? (l.lbsPerCan * gramsPerMassUnit(massUnit)) / vol : 0);
                const c = recipeConversions(gl, vol, conv, massUnit);
                return (
                  <tr key={l.ingredient}>
                    <td className="td font-medium">{l.ingredient}</td>
                    <td className="td text-right tabular-nums">{gl.toFixed(4)}</td>
                    <td className="td text-right tabular-nums text-muted">{c.ozPerL.toFixed(4)}</td>
                    <td className="td text-right tabular-nums text-muted">{c.lbsPerBbl.toFixed(4)}</td>
                    <td className="td text-right tabular-nums text-muted">{c.massPerCan.toFixed(6)}</td>
                    <td className="td text-right whitespace-nowrap">
                      <button className="btn-sm border border-edge hover:bg-paper mr-1" onClick={() => setEditIdx(i)}>Edit</button>
                      <button className="btn-sm text-danger hover:bg-danger/5" onClick={() => removeLine(i)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 && <tr><td className="td text-muted" colSpan={6}>No lines yet.</td></tr>}
            </tbody>
          </table>
          <div className="mt-3 flex items-center gap-2">
            <AddLineSelect ingredients={d.ingredients.map((i) => i.name).filter((n) => !lines.some((l) => l.ingredient === n))} onAdd={addLine} disabled={saving} />
          </div>
        </div>
      )}
      {editIdx !== null && lines[editIdx] && (
        <GlModal ingredient={lines[editIdx].ingredient} value={lines[editIdx].gPerL ?? 0} massUnit={massUnit}
          onClose={() => setEditIdx(null)}
          onSave={async (v) => { await setGL(editIdx, v); setEditIdx(null); }} />
      )}
    </div>
  );
}

function AddLineSelect({ ingredients, onAdd, disabled }: { ingredients: string[]; onAdd: (n: string) => void; disabled: boolean }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2 items-center">
      <select className="input w-auto py-1.5" value={v} onChange={(e) => setV(e.target.value)}>
        <option value="">Add ingredient…</option>
        {ingredients.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <button className="btn-primary btn-sm" disabled={disabled || !v} onClick={() => { onAdd(v); setV(""); }}>Add</button>
    </div>
  );
}

function GlModal({ ingredient, value, massUnit, onClose, onSave }: {
  ingredient: string; value: number; massUnit: string; onClose: () => void; onSave: (v: number) => void;
}) {
  const { saving } = useData();
  const [gl, setGl] = useState(String(value ?? 0));
  return (
    <Modal title={`${ingredient} — g:L`} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Grams per litre (g:L)" hint={`Drives the ${massUnit}/unit used by ordering & COGS`}>
          <input type="number" step="any" className="input" value={gl} onChange={(e) => setGl(e.target.value)} autoFocus />
        </LabeledInput>
        <div className="flex justify-end gap-2 pt-1"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={saving} onClick={() => onSave(Number(gl) || 0)}>{saving ? "…" : "Save"}</button></div>
      </div>
    </Modal>
  );
}

// ── SKUs ─────────────────────────────────────────────────────────────────────
function SkusTab() {
  const { data, mutate, saving } = useData();
  const d = data!;
  const [edit, setEdit] = useState<Sku | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center"><h3 className="text-sm font-semibold">SKUs</h3><button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ SKU</button></div>
      <div className="card overflow-x-auto">
        <table className="w-full min-w-[520px]">
          <thead><tr><th className="th">Name</th><th className="th">Flavour</th><th className="th">Strength</th><th className="th">Status</th><th className="th"></th></tr></thead>
          <tbody>
            {d.skus.map((s) => (
              <tr key={s.id}>
                <td className="td font-medium">{s.name}</td>
                <td className="td text-muted">{s.flavour}</td>
                <td className="td text-muted">{s.strength}</td>
                <td className="td">{s.discontinued ? <Badge tone="danger">Discontinued</Badge> : <Badge tone="ok">Active</Badge>}</td>
                <td className="td text-right"><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(s)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(creating || edit) && (
        <SkuModal sku={edit} saving={saving} hasAmazon={(d.business.channels || []).some((c) => c.kind === "amazon")} productTypes={d.business.productTypes || []} onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (sk) => {
            await mutate((draft) => {
              if (edit) {
                const t = draft.skus.find((x) => x.id === edit.id);
                if (t) {
                  const oldKey = `${t.flavour}|${t.strengthValue}`;
                  Object.assign(t, sk);
                  const newKey = `${t.flavour}|${t.strengthValue}`;
                  // Recipes are keyed by flavour|strength — carry the recipe over on a rename so it
                  // doesn't get stranded under the old key.
                  if (oldKey !== newKey && draft.recipes[oldKey]?.length) {
                    const sharedByOther = draft.skus.some((x) => x.id !== t.id && `${x.flavour}|${x.strengthValue}` === oldKey);
                    if (!draft.recipes[newKey]?.length) draft.recipes[newKey] = sharedByOther ? draft.recipes[oldKey].map((l) => ({ ...l })) : draft.recipes[oldKey];
                    if (!sharedByOther) delete draft.recipes[oldKey];
                  }
                }
              }
              else {
                const ns: Sku = { id: newId("sku"), ...sk };
                draft.skus.push(ns);
                const key = `${ns.flavour}|${ns.strengthValue}`;
                if (!draft.recipes[key]) draft.recipes[key] = [];
              }
            });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? () => { mutate((draft) => { draft.skus = draft.skus.filter((x) => x.id !== edit.id); }); setEdit(null); } : undefined}
        />
      )}
    </div>
  );
}

// Read any image file (PNG/JPG/WEBP/GIF/AVIF…) and return a small self-contained data URL, so the
// product thumbnail is stored inline (no external URL to break, works for owner + canner alike).
async function fileToThumb(file: File, max = 160): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("Couldn't read that image"));
      i.src = url;
    });
    const scale = Math.min(1, max / Math.max(img.width || 1, img.height || 1));
    const w = Math.max(1, Math.round((img.width || max) * scale));
    const h = Math.max(1, Math.round((img.height || max) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
    const webp = canvas.toDataURL("image/webp", 0.85);
    return webp.startsWith("data:image/webp") ? webp : canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function SkuModal({ sku, saving, hasAmazon, productTypes, onClose, onSave, onDelete }: {
  sku: Sku | null; saving: boolean; hasAmazon: boolean; productTypes: ProductType[]; onClose: () => void; onSave: (s: Omit<Sku, "id">) => void; onDelete?: () => void;
}) {
  const [flavour, setFlavour] = useState(sku?.flavour || "");
  const [strengthValue, setSv] = useState(sku?.strengthValue != null ? String(sku.strengthValue) : "");
  const [shopifySku, setShopifySku] = useState(sku?.shopifySku || "");
  const [amazonSku, setAmazonSku] = useState(sku?.amazonSku || "");
  const [amazonPackSize, setPack] = useState(sku?.amazonPackSize != null ? String(sku.amazonPackSize) : "");
  const [packSize, setPackSize] = useState(sku?.packSize != null ? String(sku.packSize) : "");
  const [volumeMl, setVolumeMl] = useState(sku?.volumeMl != null ? String(sku.volumeMl) : "");
  const [productType, setProductType] = useState(sku?.productType || (sku?.purchased ? "gummy" : "can"));
  const [adders, setAdders] = useState<{ name: string; amount: string }[]>((sku?.cogsAdders || []).map((a) => ({ name: a.name, amount: String(a.amount) })));
  const setA = (i: number, k: "name" | "amount", v: string) => setAdders((s) => s.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const [assembled, setAssembled] = useState(!!sku?.assembled);
  const [discontinued, setDisc] = useState(!!sku?.discontinued);
  const [imageUrl, setImageUrl] = useState(sku?.imageUrl || "");
  const [upBusy, setUpBusy] = useState(false);
  const [upErr, setUpErr] = useState("");
  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUpBusy(true); setUpErr("");
    try { setImageUrl(await fileToThumb(f)); }
    catch (err) { setUpErr(err instanceof Error ? err.message : "Upload failed"); }
    finally { setUpBusy(false); e.target.value = ""; }
  };
  const isData = imageUrl.startsWith("data:");
  const strength = sku?.strength && sku.strengthValue === Number(strengthValue) ? sku.strength : `${strengthValue}mg`;
  const name = `${flavour} ${strength}`.trim();
  const typeOpts = productTypes.length ? productTypes : [{ id: "can", label: "Cans" } as ProductType];
  return (
    <Modal title={sku ? "Edit SKU" : "New SKU"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Flavour"><input className="input" value={flavour} onChange={(e) => setFlavour(e.target.value)} /></LabeledInput>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Strength (mg)" hint="Use 0 for single-strength SKUs"><input type="number" className="input" value={strengthValue} onChange={(e) => setSv(e.target.value)} /></LabeledInput>
          <LabeledInput label="Product type" hint="Drives COGS make-up & pack size">
            <select className="input" value={productType} onChange={(e) => setProductType(e.target.value)}>
              {productType && !typeOpts.some((t) => t.id === productType) && <option value={productType}>{productType}</option>}
              {typeOpts.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </LabeledInput>
        </div>
        <LabeledInput label="Shopify SKU" hint="Variant SKU code for demand/inventory lookup"><input className="input" value={shopifySku} onChange={(e) => setShopifySku(e.target.value)} /></LabeledInput>
        {hasAmazon && <LabeledInput label="Amazon SKU" hint="Amazon seller-SKU (blank = same as Shopify SKU)"><input className="input" value={amazonSku} onChange={(e) => setAmazonSku(e.target.value)} /></LabeledInput>}
        {hasAmazon && <LabeledInput label="Amazon pack size" hint="Cans per Amazon SKU, e.g. 12 for a 12-pack (blank = 1)"><input type="number" className="input" value={amazonPackSize} onChange={(e) => setPack(e.target.value)} /></LabeledInput>}
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Retail pack size" hint="Cans per retail pack for distribution (UK: 12; blank = 1)"><input type="number" className="input" value={packSize} onChange={(e) => setPackSize(e.target.value)} /></LabeledInput>
          <LabeledInput label="Net volume (ml)" hint="Per-unit liquid volume, e.g. 355 (can) or 750 (spirit). Used by the label check; blank = volume not checked."><input type="number" className="input" value={volumeMl} onChange={(e) => setVolumeMl(e.target.value)} placeholder="e.g. 750" /></LabeledInput>
        </div>
        <LabeledInput label="Product image" hint="Upload any image (PNG / JPG / WEBP / GIF) or paste a public URL — shown on production run cards.">
          <div className="flex items-center gap-2">
            {imageUrl
              ? <img src={imageUrl} alt="" className="w-9 h-9 rounded-md object-cover border border-edge/60 shrink-0 bg-white" />
              : <div className="w-9 h-9 rounded-md border border-dashed border-edge shrink-0" />}
            <input className="input" value={isData ? "" : imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder={isData ? "Uploaded image ✓" : "https://…"} />
            <label className="btn-sm border border-edge hover:bg-paper cursor-pointer shrink-0">{upBusy ? "…" : "Upload"}
              <input type="file" accept="image/*,image/webp" className="hidden" onChange={handleFile} />
            </label>
            {imageUrl && <button type="button" className="btn-sm text-danger hover:bg-danger/5 shrink-0" onClick={() => setImageUrl("")}>✕</button>}
          </div>
          {upErr && <span className="block text-[11px] text-danger mt-1">{upErr}</span>}
        </LabeledInput>
        <div className="rounded-lg border border-edge/70 p-3 space-y-2">
          <div className="flex items-center justify-between"><span className="label mb-0">COGS adders (this SKU only)</span><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setAdders((s) => [...s, { name: "", amount: "" }])}>+ Adder</button></div>
          <p className="text-[11px] text-muted">Per-unit extras just for this SKU on top of its product type — e.g. XO lids on 60mg.</p>
          {adders.map((a, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className="input py-1 flex-1" placeholder="e.g. XO lid" value={a.name} onChange={(e) => setA(i, "name", e.target.value)} />
              <input type="number" step="any" className="input py-1 w-24 text-right" placeholder="0.00" value={a.amount} onChange={(e) => setA(i, "amount", e.target.value)} />
              <button className="btn-sm text-danger hover:bg-danger/5" onClick={() => setAdders((s) => s.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assembled} onChange={(e) => setAssembled(e.target.checked)} /> Assembled (e.g. Mix Pack — no recipe; distribution only)</label>
        <div className="text-xs text-muted">Name: <span className="font-medium text-ink">{name}</span> · recipe key <code>{flavour}|{strengthValue || "?"}</code></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={discontinued} onChange={(e) => setDisc(e.target.checked)} /> Discontinued</label>
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !flavour || strengthValue === ""} onClick={() => onSave({ flavour, strength, strengthValue: Number(strengthValue) || 0, name, shopifySku, amazonSku, amazonPackSize: amazonPackSize === "" ? undefined : Number(amazonPackSize), packSize: packSize === "" ? undefined : Number(packSize), volumeMl: volumeMl === "" ? undefined : Number(volumeMl), productType, cogsAdders: adders.filter((a) => a.name.trim()).map((a) => ({ name: a.name.trim(), amount: Number(a.amount) || 0 })), imageUrl: imageUrl.trim() || undefined, assembled, discontinued })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}

// ── Suppliers ────────────────────────────────────────────────────────────────
function SuppliersTab() {
  const { data, mutate, saving } = useData();
  const d = data!;
  const [edit, setEdit] = useState<Supplier | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center"><h3 className="text-sm font-semibold">Suppliers</h3><button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Supplier</button></div>
      <div className="card overflow-x-auto">
        {d.suppliers.length === 0 ? <EmptyState title="No suppliers yet" /> : (
          <table className="w-full min-w-[420px]">
            <thead><tr><th className="th">Name</th><th className="th">Contact</th><th className="th">Notes</th><th className="th"></th></tr></thead>
            <tbody>
              {d.suppliers.map((s) => (
                <tr key={s.id}><td className="td font-medium">{s.name} {s.providedByCanner && <Badge tone="brand">canner-provided</Badge>}</td><td className="td text-muted">{s.contact || "—"}</td><td className="td text-muted">{s.notes || "—"}</td>
                  <td className="td text-right"><button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(s)}>Edit</button></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {(creating || edit) && (
        <SupplierModal supplier={edit} saving={saving} onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (sp) => {
            await mutate((draft) => { if (edit) { const t = draft.suppliers.find((x) => x.id === edit.id); if (t) Object.assign(t, sp); } else draft.suppliers.push({ id: newId("sup"), ...sp }); });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? () => { mutate((draft) => { draft.suppliers = draft.suppliers.filter((x) => x.id !== edit.id); }); setEdit(null); } : undefined}
        />
      )}
    </div>
  );
}

function SupplierModal({ supplier, saving, onClose, onSave, onDelete }: {
  supplier: Supplier | null; saving: boolean; onClose: () => void; onSave: (s: Omit<Supplier, "id">) => void; onDelete?: () => void;
}) {
  const [name, setName] = useState(supplier?.name || "");
  const [contact, setContact] = useState(supplier?.contact || "");
  const [notes, setNotes] = useState(supplier?.notes || "");
  const [provided, setProvided] = useState(!!supplier?.providedByCanner);
  return (
    <Modal title={supplier ? "Edit supplier" : "New supplier"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></LabeledInput>
        <LabeledInput label="Contact"><input className="input" value={contact} onChange={(e) => setContact(e.target.value)} /></LabeledInput>
        <LabeledInput label="Notes"><input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} /></LabeledInput>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5" checked={provided} onChange={(e) => setProvided(e.target.checked)} /> <span>Provided by canner/co-packer<span className="block text-muted text-xs">We don't order these ingredients (the canner supplies them), but they still count in COGS (on-charged).</span></span></label>
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !name} onClick={() => onSave({ name, contact, notes, providedByCanner: provided })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}

// ── Canners ──────────────────────────────────────────────────────────────────
function CannersTab() {
  const { data, mutate, saving } = useData();
  const d = data!;
  const [edit, setEdit] = useState<Canner | null>(null);
  const [creating, setCreating] = useState(false);
  const link = (token: string) => `${location.origin}/?ct=${token}`;
  const [copied, setCopied] = useState("");

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center"><h3 className="text-sm font-semibold">Canners &amp; access links</h3><button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Canner</button></div>
      <p className="text-muted text-xs">Each canner gets a private link into a simple two-tab view — <span className="font-medium">Raw Materials</span> (confirm deliveries + on-site stock) and <span className="font-medium">Production</span> (run pipeline: start, record made/sent/kept, ship). No password.</p>
      {d.canners.length === 0 ? <div className="card"><EmptyState title="No canners yet" /></div> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {d.canners.map((c) => (
            <div key={c.id} className="card space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-medium text-sm">{c.name}</div>{c.facility && <div className="text-xs text-muted">{c.facility}</div>}</div>
                <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit(c)}>Edit</button>
              </div>
              {c.contact && <div className="text-xs text-muted">{c.contact}</div>}
              <div className="flex items-center gap-2">
                <input readOnly className="input text-xs py-1" value={link(c.token)} onFocus={(e) => e.target.select()} />
                <button className="btn-sm border border-edge hover:bg-paper shrink-0" onClick={() => { navigator.clipboard?.writeText(link(c.token)); setCopied(c.id); setTimeout(() => setCopied(""), 1500); }}>{copied === c.id ? "Copied" : "Copy"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(creating || edit) && (
        <CannerModal canner={edit} saving={saving} onClose={() => { setCreating(false); setEdit(null); }}
          onSave={async (c) => {
            await mutate((draft) => {
              if (edit) { const t = draft.canners.find((x) => x.id === edit.id); if (t) Object.assign(t, c); }
              else draft.canners.push({ id: newId("can"), token: `ct_${newId("t").slice(2)}${Math.random().toString(36).slice(2, 10)}`, ...c });
            });
            setCreating(false); setEdit(null);
          }}
          onDelete={edit ? () => { mutate((draft) => { draft.canners = draft.canners.filter((x) => x.id !== edit.id); }); setEdit(null); } : undefined}
        />
      )}
    </div>
  );
}

function CannerModal({ canner, saving, onClose, onSave, onDelete }: {
  canner: Canner | null; saving: boolean; onClose: () => void; onSave: (c: { name: string; facility?: string; contact?: string }) => void; onDelete?: () => void;
}) {
  const [name, setName] = useState(canner?.name || "");
  const [facility, setFacility] = useState(canner?.facility || "");
  const [contact, setContact] = useState(canner?.contact || "");
  return (
    <Modal title={canner ? "Edit canner" : "New canner"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></LabeledInput>
        <LabeledInput label="Facility / address"><input className="input" value={facility} onChange={(e) => setFacility(e.target.value)} /></LabeledInput>
        <LabeledInput label="Contact"><input className="input" value={contact} onChange={(e) => setContact(e.target.value)} /></LabeledInput>
        {canner && <div className="text-xs text-muted">Access token is fixed once created. Delete &amp; recreate to rotate it.</div>}
        <div className="flex justify-between pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2"><button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !name} onClick={() => onSave({ name, facility, contact })}>{saving ? "…" : "Save"}</button></div>
        </div>
      </div>
    </Modal>
  );
}
